import { BaseGenerator } from './BaseGenerator.js';
import { RoomDecorator } from './RoomDecorator.js';
import { ZONE } from './ZoneTypes.js';
import { MapPipeline } from './MapPipeline.js';

/**
 * Dungeon map generator using BSP (Binary Space Partition) + corridor connection.
 * Produces rooms connected by corridors with optional decorations.
 *
 * Supports three quality modes:
 * - "draft"    — direct tile ID output (legacy, fast)
 * - "standard" — pipeline with transition + cleanup + validation
 * - "high"     — pipeline + full decoration + validation with repair
 */
export class DungeonGenerator extends BaseGenerator {
  generate(content) {
    const quality = this.options.quality || 'draft';

    if (quality === 'draft') {
      return this._generateDraft(content);
    }

    return this._generatePipeline(content, quality);
  }

  // ---- Pipeline mode (standard / high) ----

  _generatePipeline(content, quality) {
    const tiles = content.tiles || DungeonGenerator.DEFAULT_TILES;
    const opts = { ...DungeonGenerator.DEFAULT_OPTIONS, ...this.options };

    // 1. Structure pass — output zone map
    const ground = this.createGrid(this.width, this.height, ZONE.WALL);
    const objects = this.createGrid(this.width, this.height, ZONE.EMPTY);
    const overhead = this.createGrid(this.width, this.height, ZONE.EMPTY);

    // BSP partition
    const partitions = this._bspPartition(
      1, 1, this.width - 2, this.height - 2,
      opts.minRoomSize, opts.maxDepth
    );

    // Create rooms in partitions
    const rooms = [];
    for (const part of partitions) {
      const room = this._createRoom(part, opts.minRoomSize, opts.roomPadding);
      if (room) {
        rooms.push(room);
        this._carveRoomZone(ground, room);
      }
    }

    // Connect rooms with corridors
    this._connectRoomsZone(ground, rooms, opts);

    // Add doors
    if (opts.doors) {
      this._placeDoorsZone(ground, rooms);
    }

    // Wall tops
    this._addWallTops(ground);

    // 2. Run pipeline
    const pipeline = MapPipeline.createDefault(this.rng, { tileMeta: this.options.tileMeta });

    if (quality !== 'high') {
      pipeline.removePass('decoration');
    }

    const pipelineOptions = {
      tileMapping: this._buildTileMapping(tiles),
      floorTileIds: [ZONE.FLOOR, ZONE.DOOR],
      corridorTileId: ZONE.FLOOR,
      cleanup: { minPatchSize: 0, smoothIterations: 0 },
      validation: {
        floorTileIds: [ZONE.FLOOR, ZONE.DOOR],
        corridorTileId: ZONE.FLOOR,
        repair: true,
        ensureExits: false,
      },
    };

    if (quality === 'high' && tiles.torch) {
      pipelineOptions.decorations = [
        { tileId: ZONE.TORCH, density: 0.01, spacing: opts.torchSpacing || 6, validGround: [ZONE.WALL], avoidEdges: false },
      ];
    }

    const result = pipeline.run({
      ground,
      objects,
      overhead,
      zones: ground,
      width: this.width,
      height: this.height,
      metadata: { rooms },
    }, pipelineOptions);

    // 3. Resolve zones → tile IDs
    const finalGround = this._resolveZones(result.ground, tiles);
    const finalObjects = this._resolveObjectZones(result.objects, tiles);
    const finalOverhead = result.overhead.map(row => [...row]);

    // 4. Decorate rooms (same as draft, operates on tile IDs)
    if (opts.decorate && content.roomTemplates) {
      const decorator = new RoomDecorator(this.rng);
      for (const room of rooms) {
        const template = this.rng.pick(content.roomTemplates);
        decorator.decorate(finalObjects, room, template);
      }
    }

    // Wall torch placement (on resolved tile IDs)
    this._placeWallDecor(finalObjects, finalGround, tiles, opts);

    // Collision data
    const collision = finalGround.map(row =>
      row.map(tile => tile === tiles.wall || tile === tiles.wallTop ? 1 : 0)
    );

    if (tiles._tileLayers) {
      this.redistributeByLayer(
        { ground: finalGround, objects: finalObjects, overhead: finalOverhead },
        tiles._tileLayers,
      );
    }

    return this.buildTMJ({
      layers: [
        { name: 'Ground', data: this.flattenGrid(finalGround) },
        { name: 'Objects', data: this.flattenGrid(finalObjects) },
        { name: 'Overhead', data: this.flattenGrid(finalOverhead) },
      ],
      tilesets: content.tilesets || [],
      properties: {
        rooms: { type: 'string', value: JSON.stringify(rooms) },
        collision: { type: 'string', value: JSON.stringify(this.flattenGrid(collision)) },
      },
    });
  }

  _carveRoomZone(grid, room) {
    for (let y = room.y; y < room.y + room.height; y++) {
      for (let x = room.x; x < room.x + room.width; x++) {
        this.setCell(grid, x, y, ZONE.FLOOR);
      }
    }
  }

  _connectRoomsZone(grid, rooms, opts) {
    if (rooms.length < 2) return;

    for (let i = 0; i < rooms.length - 1; i++) {
      const a = rooms[i];
      const b = rooms[i + 1];
      const ax = Math.floor(a.x + a.width / 2);
      const ay = Math.floor(a.y + a.height / 2);
      const bx = Math.floor(b.x + b.width / 2);
      const by = Math.floor(b.y + b.height / 2);
      this._carveCorridorZone(grid, ax, ay, bx, by, opts.corridorWidth || 1);
    }

    if (opts.extraCorridors && rooms.length > 3) {
      const extra = this.rng.nextInt(1, Math.floor(rooms.length / 3));
      for (let i = 0; i < extra; i++) {
        const a = this.rng.pick(rooms);
        const b = this.rng.pick(rooms);
        if (a === b) continue;
        const ax = Math.floor(a.x + a.width / 2);
        const ay = Math.floor(a.y + a.height / 2);
        const bx = Math.floor(b.x + b.width / 2);
        const by = Math.floor(b.y + b.height / 2);
        this._carveCorridorZone(grid, ax, ay, bx, by, opts.corridorWidth || 1);
      }
    }
  }

  _carveCorridorZone(grid, x0, y0, x1, y1, width) {
    const half = Math.floor(width / 2);

    if (this.rng.chance(0.5)) {
      const step = x0 < x1 ? 1 : -1;
      for (let x = x0; x !== x1; x += step) {
        for (let d = -half; d <= half; d++) {
          if (this.getCell(grid, x, y0 + d) !== ZONE.FLOOR) {
            this.setCell(grid, x, y0 + d, ZONE.FLOOR);
          }
        }
      }
      const stepY = y0 < y1 ? 1 : -1;
      for (let y = y0; y !== y1 + stepY; y += stepY) {
        for (let d = -half; d <= half; d++) {
          if (this.getCell(grid, x1 + d, y) !== ZONE.FLOOR) {
            this.setCell(grid, x1 + d, y, ZONE.FLOOR);
          }
        }
      }
    } else {
      const stepY = y0 < y1 ? 1 : -1;
      for (let y = y0; y !== y1; y += stepY) {
        for (let d = -half; d <= half; d++) {
          if (this.getCell(grid, x0 + d, y) !== ZONE.FLOOR) {
            this.setCell(grid, x0 + d, y, ZONE.FLOOR);
          }
        }
      }
      const step = x0 < x1 ? 1 : -1;
      for (let x = x0; x !== x1 + step; x += step) {
        for (let d = -half; d <= half; d++) {
          if (this.getCell(grid, x, y1 + d) !== ZONE.FLOOR) {
            this.setCell(grid, x, y1 + d, ZONE.FLOOR);
          }
        }
      }
    }
  }

  _placeDoorsZone(ground, rooms) {
    for (const room of rooms) {
      const edges = [];
      for (let x = room.x; x < room.x + room.width; x++) {
        edges.push({ x, y: room.y - 1, dir: 'north' });
        edges.push({ x, y: room.y + room.height, dir: 'south' });
      }
      for (let y = room.y; y < room.y + room.height; y++) {
        edges.push({ x: room.x - 1, y, dir: 'west' });
        edges.push({ x: room.x + room.width, y, dir: 'east' });
      }

      for (const edge of edges) {
        if (this.getCell(ground, edge.x, edge.y) === ZONE.FLOOR) {
          if (this.rng.chance(0.5)) {
            this.setCell(ground, edge.x, edge.y, ZONE.DOOR);
            // Validate: door must have floor on both sides (horizontally or vertically)
            const hFloor = this.getCell(ground, edge.x - 1, edge.y) === ZONE.FLOOR && this.getCell(ground, edge.x + 1, edge.y) === ZONE.FLOOR;
            const vFloor = this.getCell(ground, edge.x, edge.y - 1) === ZONE.FLOOR && this.getCell(ground, edge.x, edge.y + 1) === ZONE.FLOOR;
            if (!hFloor && !vFloor) {
              this.setCell(ground, edge.x, edge.y, ZONE.WALL);
            }
          }
        }
      }
    }
  }

  _addWallTops(ground) {
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        if (ground[y][x] !== ZONE.WALL) continue;
        // Check if any adjacent floor/door tile exists below or diagonally below
        const hasFloorBelow = this.getCell(ground, x, y + 1) === ZONE.FLOOR
          || this.getCell(ground, x, y + 1) === ZONE.DOOR;
        const hasFloorDiagBL = this.getCell(ground, x - 1, y + 1) === ZONE.FLOOR;
        const hasFloorDiagBR = this.getCell(ground, x + 1, y + 1) === ZONE.FLOOR;
        if (hasFloorBelow || hasFloorDiagBL || hasFloorDiagBR) {
          ground[y][x] = ZONE.WALL_TOP;
        }
      }
    }
  }

  _buildTileMapping(tiles) {
    return {
      [ZONE.WALL]:     { center: tiles.wall },
      [ZONE.WALL_TOP]: { center: tiles.wallTop || tiles.wall },
      [ZONE.FLOOR]:    { center: tiles.floor },
      [ZONE.DOOR]:     { center: tiles.door || tiles.floor },
    };
  }

  _resolveZones(grid, tiles) {
    const mapping = {
      [ZONE.WALL]:     tiles.wall,
      [ZONE.WALL_TOP]: tiles.wallTop || tiles.wall,
      [ZONE.FLOOR]:    tiles.floor,
      [ZONE.DOOR]:     tiles.door || tiles.floor,
    };
    return grid.map(row => row.map(z => mapping[z] !== undefined ? mapping[z] : z));
  }

  _resolveObjectZones(grid, tiles) {
    const mapping = {
      [ZONE.TORCH]: tiles.torch || 0,
    };
    return grid.map(row => row.map(z => {
      if (z === ZONE.EMPTY || z === 0) return 0;
      return mapping[z] !== undefined ? mapping[z] : z;
    }));
  }

  // ---- Draft mode (original direct tile output) ----

  _generateDraft(content) {
    const tiles = content.tiles || DungeonGenerator.DEFAULT_TILES;
    const opts = { ...DungeonGenerator.DEFAULT_OPTIONS, ...this.options };

    const ground = this.createGrid(this.width, this.height, tiles.wall);
    const objects = this.createGrid(this.width, this.height, 0);
    const overhead = this.createGrid(this.width, this.height, 0);

    const partitions = this._bspPartition(
      1, 1, this.width - 2, this.height - 2,
      opts.minRoomSize, opts.maxDepth
    );

    const rooms = [];
    for (const part of partitions) {
      const room = this._createRoom(part, opts.minRoomSize, opts.roomPadding);
      if (room) {
        rooms.push(room);
        this._carveRoom(ground, room, tiles);
      }
    }

    this._connectRooms(ground, rooms, tiles, opts);

    if (opts.doors) {
      this._placeDoors(ground, rooms, tiles);
    }

    if (opts.decorate && content.roomTemplates) {
      const decorator = new RoomDecorator(this.rng);
      for (const room of rooms) {
        const template = this.rng.pick(content.roomTemplates);
        decorator.decorate(objects, room, template);
      }
    }

    this._placeWallDecor(objects, ground, tiles, opts);

    const collision = ground.map(row =>
      row.map(tile => tile === tiles.wall || tile === tiles.wallTop ? 1 : 0)
    );

    if (tiles._tileLayers) {
      this.redistributeByLayer(
        { ground, objects, overhead },
        tiles._tileLayers,
      );
    }

    return this.buildTMJ({
      layers: [
        { name: 'Ground', data: this.flattenGrid(ground) },
        { name: 'Objects', data: this.flattenGrid(objects) },
        { name: 'Overhead', data: this.flattenGrid(overhead) },
      ],
      tilesets: content.tilesets || [],
      properties: {
        rooms: { type: 'string', value: JSON.stringify(rooms) },
        collision: { type: 'string', value: JSON.stringify(this.flattenGrid(collision)) },
      },
    });
  }

  // ---- Shared helpers ----

  _bspPartition(x, y, w, h, minSize, depth) {
    if (depth <= 0 || w < minSize * 2 + 3 || h < minSize * 2 + 3) {
      return [{ x, y, w, h }];
    }

    let splitH;
    if (w > h * 1.3) splitH = false;
    else if (h > w * 1.3) splitH = true;
    else splitH = this.rng.chance(0.5);

    if (splitH) {
      const split = this.rng.nextInt(y + minSize + 1, y + h - minSize - 2);
      return [
        ...this._bspPartition(x, y, w, split - y, minSize, depth - 1),
        ...this._bspPartition(x, split, w, y + h - split, minSize, depth - 1),
      ];
    } else {
      const split = this.rng.nextInt(x + minSize + 1, x + w - minSize - 2);
      return [
        ...this._bspPartition(x, y, split - x, h, minSize, depth - 1),
        ...this._bspPartition(split, y, x + w - split, h, minSize, depth - 1),
      ];
    }
  }

  _createRoom(partition, minSize, padding) {
    const maxW = partition.w - padding * 2;
    const maxH = partition.h - padding * 2;
    if (maxW < minSize || maxH < minSize) return null;

    const roomW = this.rng.nextInt(minSize, maxW);
    const roomH = this.rng.nextInt(minSize, maxH);
    const roomX = partition.x + this.rng.nextInt(padding, partition.w - roomW - padding);
    const roomY = partition.y + this.rng.nextInt(padding, partition.h - roomH - padding);

    return { x: roomX, y: roomY, width: roomW, height: roomH };
  }

  _carveRoom(grid, room, tiles) {
    for (let y = room.y; y < room.y + room.height; y++) {
      for (let x = room.x; x < room.x + room.width; x++) {
        this.setCell(grid, x, y, tiles.floor);
      }
    }
    if (tiles.wallTop) {
      for (let x = room.x - 1; x <= room.x + room.width; x++) {
        const wy = room.y - 1;
        if (this.getCell(grid, x, wy) === tiles.wall) {
          this.setCell(grid, x, wy, tiles.wallTop);
        }
      }
    }
  }

  _connectRooms(grid, rooms, tiles, opts) {
    if (rooms.length < 2) return;

    for (let i = 0; i < rooms.length - 1; i++) {
      const a = rooms[i];
      const b = rooms[i + 1];
      const ax = Math.floor(a.x + a.width / 2);
      const ay = Math.floor(a.y + a.height / 2);
      const bx = Math.floor(b.x + b.width / 2);
      const by = Math.floor(b.y + b.height / 2);
      this._carveCorridor(grid, ax, ay, bx, by, tiles, opts.corridorWidth || 1);
    }

    if (opts.extraCorridors && rooms.length > 3) {
      const extra = this.rng.nextInt(1, Math.floor(rooms.length / 3));
      for (let i = 0; i < extra; i++) {
        const a = this.rng.pick(rooms);
        const b = this.rng.pick(rooms);
        if (a === b) continue;
        const ax = Math.floor(a.x + a.width / 2);
        const ay = Math.floor(a.y + a.height / 2);
        const bx = Math.floor(b.x + b.width / 2);
        const by = Math.floor(b.y + b.height / 2);
        this._carveCorridor(grid, ax, ay, bx, by, tiles, opts.corridorWidth || 1);
      }
    }
  }

  _carveCorridor(grid, x0, y0, x1, y1, tiles, width) {
    const half = Math.floor(width / 2);

    if (this.rng.chance(0.5)) {
      const step = x0 < x1 ? 1 : -1;
      for (let x = x0; x !== x1; x += step) {
        for (let d = -half; d <= half; d++) {
          if (this.getCell(grid, x, y0 + d) !== tiles.floor) {
            this.setCell(grid, x, y0 + d, tiles.floor);
          }
        }
      }
      const stepY = y0 < y1 ? 1 : -1;
      for (let y = y0; y !== y1 + stepY; y += stepY) {
        for (let d = -half; d <= half; d++) {
          if (this.getCell(grid, x1 + d, y) !== tiles.floor) {
            this.setCell(grid, x1 + d, y, tiles.floor);
          }
        }
      }
    } else {
      const stepY = y0 < y1 ? 1 : -1;
      for (let y = y0; y !== y1; y += stepY) {
        for (let d = -half; d <= half; d++) {
          if (this.getCell(grid, x0 + d, y) !== tiles.floor) {
            this.setCell(grid, x0 + d, y, tiles.floor);
          }
        }
      }
      const step = x0 < x1 ? 1 : -1;
      for (let x = x0; x !== x1 + step; x += step) {
        for (let d = -half; d <= half; d++) {
          if (this.getCell(grid, x, y1 + d) !== tiles.floor) {
            this.setCell(grid, x, y1 + d, tiles.floor);
          }
        }
      }
    }
  }

  _placeDoors(ground, rooms, tiles) {
    for (const room of rooms) {
      const edges = [];
      for (let x = room.x; x < room.x + room.width; x++) {
        edges.push({ x, y: room.y - 1, dir: 'north' });
        edges.push({ x, y: room.y + room.height, dir: 'south' });
      }
      for (let y = room.y; y < room.y + room.height; y++) {
        edges.push({ x: room.x - 1, y, dir: 'west' });
        edges.push({ x: room.x + room.width, y, dir: 'east' });
      }

      for (const edge of edges) {
        if (this.getCell(ground, edge.x, edge.y) === tiles.floor) {
          if (tiles.door && this.rng.chance(0.5)) {
            this.setCell(ground, edge.x, edge.y, tiles.door);
            // Validate: door must have floor on both sides (horizontally or vertically)
            const hFloor = this.getCell(ground, edge.x - 1, edge.y) === tiles.floor && this.getCell(ground, edge.x + 1, edge.y) === tiles.floor;
            const vFloor = this.getCell(ground, edge.x, edge.y - 1) === tiles.floor && this.getCell(ground, edge.x, edge.y + 1) === tiles.floor;
            if (!hFloor && !vFloor) {
              this.setCell(ground, edge.x, edge.y, tiles.wall);
            }
          }
        }
      }
    }
  }

  _placeWallDecor(objects, ground, tiles, opts) {
    if (!tiles.torch) return;
    const spacing = opts.torchSpacing || 6;

    for (let y = 1; y < this.height - 1; y++) {
      for (let x = 1; x < this.width - 1; x++) {
        if (ground[y][x] !== tiles.floor) continue;
        if (ground[y - 1]?.[x] === tiles.wall && x % spacing === 0) {
          this.setCell(objects, x, y - 1, tiles.torch);
        }
      }
    }
  }

  static DEFAULT_TILES = {
    wall: 1,
    wallTop: 2,
    floor: 3,
    door: 4,
    torch: 5,
  };

  static DEFAULT_OPTIONS = {
    minRoomSize: 4,
    maxDepth: 5,
    roomPadding: 1,
    corridorWidth: 1,
    doors: true,
    extraCorridors: true,
    decorate: true,
    torchSpacing: 6,
  };

  static getEmittedZones() {
    return [
      ZONE.WALL, ZONE.WALL_TOP, ZONE.FLOOR, ZONE.DOOR, ZONE.TORCH,
    ];
  }
}
